import { useMemo } from 'react';

import type { TPricingElement, WebTheme } from '@shared/dream-components';

import type { Tier } from '~/interfaces/subscriptions';
import { buildStandalonePricingNode } from '~/utils/buildStandalonePricingNode';
import { getContrastingColor, hasMinimumContrast } from '~/utils/getContrastingColor';

interface Params {
  tiers: Tier[];
  theme: WebTheme | undefined;
  checkoutCta?: string;
}

/**
 * Static pricing node for the upgrade modals. Expects the tiers already
 * scoped by the caller's purchasable-tiers hook; returns `null` while the theme isn't
 * ready or when there is nothing to sell.
 */
export const useUpgradePricingNode = ({ tiers, theme, checkoutCta }: Params): TPricingElement | null => {
  return useMemo(() => {
    if (!theme || tiers.length === 0) {
      return null;
    }

    const node = buildStandalonePricingNode({ id: 'community-upgrade-pricing', tiers, theme });

    // The card stays white whatever the community colors are, so a light primary
    // color would hide the checkmarks and the selected card border.
    const accentOnCardColor = hasMinimumContrast(theme.primary_color, node.attrs.cardBackgroundColor)
      ? theme.primary_color
      : getContrastingColor(node.attrs.cardBackgroundColor);

    // recolor the accents with the community theme, at the same accent level the
    // other community modals use (PillButton primary)
    return {
      ...node,
      attrs: {
        ...node.attrs,
        buttonColor: theme.primary_color,
        buttonTextColor: theme.text_on_primary_color,
        cardSelectedBorderColor: accentOnCardColor,
        featureIconColor: accentOnCardColor,
        featureSelectedIconColor: accentOnCardColor,
        internalCheckoutCta: checkoutCta,
        recommendedBackgroundColor: theme.primary_color,
        recommendedTextColor: theme.text_on_primary_color,
        renderDiscountPriceInline: true,

        // Checkout form in the pill style of PillTextInput/PillButton.
        // 24px -> half of height of the email input, so they look similar
        inputBorderRadius: '24px',
        inputBorderColor: 'rgba(0, 0, 0, 0.1)',
        inputTextColor: '#000000',
        inputPlaceholderColor: 'rgba(0, 0, 0, 0.3)',
        buttonBorderRadius: '9999px',
      },
    };
  }, [tiers, theme, checkoutCta]);
};
